/**
 * Tasks page — Monday-style board for embedded task management.
 *
 * Features (MVP that already feels cool):
 *   • Board view grouped by status / priority / assignee (toggleable)
 *   • Inline "+ Add task" at the bottom of each group
 *   • Click-to-cycle status pill (todo → in_progress → blocked → done)
 *   • Click-to-cycle priority dot (low → medium → high → critical)
 *   • Assignee chip with initials avatar
 *   • Overdue dates pulse red
 *   • Slide-over drawer with description, comments, full activity log
 *   • Real-time-ish: page re-fetches after every mutation
 *   • Search + filter strip (status, assignee, priority)
 *   • Mobile-first: groups stack vertically, rows scale to 1-column
 *
 * All test IDs use the `tasks-*` prefix.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, X, ChevronDown, MessageSquare, Clock, AlertCircle, Send, Trash2, User as UserIcon, Inbox as InboxIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  STATUS_META, STATUS_ORDER, PRIORITY_META, PRIORITY_ORDER,
  Avatar, StatusPill, PriorityDot,
  isOverdue, formatDueDate,
} from '@/components/TaskBits';

// ─────────────────── Task row ───────────────────

function TaskRow({ task, onUpdate, onOpen, assignees }) {
  const overdue = isOverdue(task.due_date, task.status);

  const cycleStatus = (e) => {
    e.stopPropagation();
    const idx = STATUS_ORDER.indexOf(task.status);
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    onUpdate(task.task_id, { status: next });
  };
  const cyclePriority = (e) => {
    e.stopPropagation();
    const idx = PRIORITY_ORDER.indexOf(task.priority);
    const next = PRIORITY_ORDER[(idx + 1) % PRIORITY_ORDER.length];
    onUpdate(task.task_id, { priority: next });
  };
  return (
    <div
      data-testid={`tasks-row-${task.task_id}`}
      onClick={() => onOpen(task)}
      className="group grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-t border-slate-100 dark:border-white/5 hover:bg-violet-50/40 dark:hover:bg-white/[0.03] cursor-pointer transition-colors"
    >
      <div className="min-w-0">
        <div className={`text-sm font-semibold truncate ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
          {task.title}
        </div>
        {task.description && (
          <div className="text-[11px] text-slate-500 dark:text-white/45 truncate mt-0.5">
            {task.description.slice(0, 80)}{task.description.length > 80 ? '…' : ''}
          </div>
        )}
        {/* Mobile-only inline meta row */}
        <div className="mt-1.5 flex items-center gap-2 sm:hidden">
          <StatusPill status={task.status} onClick={cycleStatus} testid={`tasks-row-status-${task.task_id}`} />
          <PriorityDot priority={task.priority} onClick={cyclePriority} testid={`tasks-row-priority-${task.task_id}`} />
          {task.due_date && (
            <span className={`text-[10px] uppercase tracking-[0.14em] font-bold ${overdue ? 'text-rose-500 animate-pulse' : 'text-slate-500'}`}>
              <Clock size={9} className="inline mr-1" />{formatDueDate(task.due_date)}
            </span>
          )}
          <Avatar name={task.assignee_name} size="sm" />
          {task.comments?.length > 0 && (
            <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
              <MessageSquare size={9} /> {task.comments.length}
            </span>
          )}
        </div>
      </div>
      <div className="hidden sm:block">
        <StatusPill status={task.status} onClick={cycleStatus} testid={`tasks-row-status-${task.task_id}`} />
      </div>
      <div className="hidden sm:block">
        <PriorityDot priority={task.priority} onClick={cyclePriority} testid={`tasks-row-priority-${task.task_id}`} />
      </div>
      <div className="hidden sm:flex items-center gap-2 min-w-[90px]">
        <Avatar name={task.assignee_name} size="sm" />
        <span className="text-[11px] text-slate-500 dark:text-white/55 truncate max-w-[100px]">
          {task.assignee_name || 'Unassigned'}
        </span>
      </div>
      <div className="hidden sm:block">
        {task.due_date && (
          <span className={`text-[10px] uppercase tracking-[0.14em] font-bold whitespace-nowrap inline-flex items-center gap-1 ${overdue ? 'text-rose-500 animate-pulse' : 'text-slate-500 dark:text-white/55'}`}>
            <Clock size={10} />{formatDueDate(task.due_date)}
          </span>
        )}
      </div>
      <div className="sm:hidden text-slate-300 dark:text-white/30 self-start mt-1">
        <ChevronDown size={14} className="-rotate-90" />
      </div>
    </div>
  );
}

// ─────────────────── Group ───────────────────

function Group({ group, onUpdate, onOpen, onAddTask }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const meta = STATUS_META[group.key] || { color: '#5E1F8B', bg: '#F5E9FE' };

  const submit = (e) => {
    e?.preventDefault?.();
    const t = title.trim();
    if (!t) return;
    const isStatusGroup = !!STATUS_META[group.key];
    onAddTask({
      title: t,
      status: isStatusGroup ? group.key : 'todo',
      priority: 'medium',
      group: !isStatusGroup ? group.label : 'General',
    });
    setTitle('');
    setAdding(false);
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-white/[0.02]" data-testid={`tasks-group-${group.key}`}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 bg-slate-50 dark:bg-white/[0.03] hover:bg-slate-100 dark:hover:bg-white/[0.05] transition-colors"
      >
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
        <span className="font-bold text-sm text-slate-900 dark:text-white">{group.label}</span>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 ml-auto sm:ml-0">
          {group.count}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-400 sm:ml-auto transition-transform ${collapsed ? '-rotate-90' : ''}`}
        />
      </button>
      {!collapsed && (
        <>
          {group.tasks.length === 0 && !adding && (
            <div className="px-4 py-4 text-[11px] text-slate-400 dark:text-white/30 italic">No tasks yet.</div>
          )}
          {group.tasks.map((t) => (
            <TaskRow
              key={t.task_id}
              task={t}
              onUpdate={onUpdate}
              onOpen={onOpen}
            />
          ))}
          {adding ? (
            <form onSubmit={submit} className="px-3 sm:px-4 py-2.5 border-t border-slate-100 dark:border-white/5 flex items-center gap-2">
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => { if (!title.trim()) setAdding(false); }}
                placeholder="Task title — press Enter to add"
                className="flex-1 text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1.5 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
                data-testid="tasks-quick-add-input"
              />
              <button type="submit" className="text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB]" data-testid="tasks-quick-add-submit">
                Add
              </button>
              <button type="button" onClick={() => { setTitle(''); setAdding(false); }} className="text-slate-400 hover:text-slate-700">
                <X size={14} />
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid={`tasks-add-${group.key}`}
              className="w-full px-3 sm:px-4 py-2.5 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 hover:text-[var(--ve-violet-bright)] hover:bg-violet-50/30 dark:hover:bg-white/[0.02] transition-colors border-t border-slate-100 dark:border-white/5 flex items-center gap-2"
            >
              <Plus size={12} />
              Add task
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────── Drawer ───────────────────

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on prop-sync */
function TaskDrawer({ task, assignees, onClose, onUpdate, onDelete, onComment }) {
  const [comment, setComment] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(task.description || '');
  useEffect(() => { setDesc(task.description || ''); }, [task.task_id]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
      data-testid="tasks-drawer"
    >
      <aside
        className="w-full sm:w-[480px] h-full bg-white dark:bg-[#0A0A0A] shadow-2xl overflow-y-auto border-l border-slate-200 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-md border-b border-slate-200 dark:border-white/10 px-5 py-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
              Task · {task.task_id.slice(-6)}
            </div>
            <input
              defaultValue={task.title}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next && next !== task.title) onUpdate(task.task_id, { title: next });
              }}
              data-testid="tasks-drawer-title"
              className="w-full mt-1 text-lg font-bold bg-transparent border-0 focus:outline-none text-slate-900 dark:text-white"
            />
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white -mr-1" data-testid="tasks-drawer-close">
            <X size={18} />
          </button>
        </div>
        {/* Meta strip */}
        <div className="px-5 py-4 space-y-3 border-b border-slate-100 dark:border-white/5">
          <Field label="Status">
            <StatusPill
              status={task.status}
              onClick={() => {
                const idx = STATUS_ORDER.indexOf(task.status);
                onUpdate(task.task_id, { status: STATUS_ORDER[(idx + 1) % STATUS_ORDER.length] });
              }}
              testid="tasks-drawer-status"
            />
          </Field>
          <Field label="Priority">
            <PriorityDot
              priority={task.priority}
              onClick={() => {
                const idx = PRIORITY_ORDER.indexOf(task.priority);
                onUpdate(task.task_id, { priority: PRIORITY_ORDER[(idx + 1) % PRIORITY_ORDER.length] });
              }}
              testid="tasks-drawer-priority"
            />
          </Field>
          <Field label="Assignee">
            <select
              value={task.assignee_user_id || ''}
              onChange={(e) => onUpdate(task.task_id, { assignee_user_id: e.target.value || null })}
              className="text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
              data-testid="tasks-drawer-assignee"
            >
              <option value="">Unassigned</option>
              {assignees.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due date">
            <input
              type="date"
              value={task.due_date || ''}
              onChange={(e) => onUpdate(task.task_id, { due_date: e.target.value || null })}
              className="text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
              data-testid="tasks-drawer-due"
            />
          </Field>
        </div>
        {/* Description */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-white/5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-2">Description</div>
          {editingDesc ? (
            <div className="space-y-2">
              <textarea
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                className="w-full text-sm border border-slate-200 dark:border-white/10 rounded p-2 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
                data-testid="tasks-drawer-desc-textarea"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { onUpdate(task.task_id, { description: desc }); setEditingDesc(false); }}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB]"
                  data-testid="tasks-drawer-desc-save"
                >
                  Save
                </button>
                <button
                  onClick={() => { setDesc(task.description || ''); setEditingDesc(false); }}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-slate-600 dark:text-white/70"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingDesc(true)}
              className="w-full text-left text-sm text-slate-700 dark:text-white/80 hover:bg-slate-50 dark:hover:bg-white/[0.02] rounded p-2 -m-2 transition-colors whitespace-pre-wrap"
              data-testid="tasks-drawer-desc"
            >
              {task.description || <em className="text-slate-400">Click to add a description…</em>}
            </button>
          )}
        </div>
        {/* Comments */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-white/5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-3">
            Comments · {task.comments?.length || 0}
          </div>
          <div className="space-y-3 mb-3">
            {(task.comments || []).map((c) => (
              <div key={c.comment_id} className="flex gap-2.5">
                <Avatar name={c.author_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-white/55">
                    {c.author_name}
                    <span className="text-slate-400 dark:text-white/35 ml-2 normal-case tracking-normal">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 dark:text-white/85 mt-0.5 whitespace-pre-wrap">{c.body}</div>
                </div>
              </div>
            ))}
            {(!task.comments || task.comments.length === 0) && (
              <div className="text-[11px] text-slate-400 italic">No comments yet.</div>
            )}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!comment.trim()) return;
              onComment(task.task_id, comment.trim());
              setComment('');
            }}
            className="flex gap-2 items-end"
          >
            <textarea
              rows={2}
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="flex-1 text-sm border border-slate-200 dark:border-white/10 rounded p-2 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
              data-testid="tasks-drawer-comment-input"
            />
            <button type="submit" className="px-3 py-2 rounded bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB] disabled:opacity-50" disabled={!comment.trim()} data-testid="tasks-drawer-comment-submit">
              <Send size={14} />
            </button>
          </form>
        </div>
        {/* Activity */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-white/5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-3">Activity</div>
          <div className="space-y-2 text-[11px]">
            {(task.activity || []).slice().reverse().map((a) => (
              <div key={a.event_id} className="flex gap-2 items-start">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--ve-violet-bright)] mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-slate-700 dark:text-white/75">{a.actor_name}</span>
                  <span className="text-slate-500 dark:text-white/55"> {a.action.replace(/_/g, ' ')}</span>
                  {a.from_value && a.to_value && (
                    <span className="text-slate-500 dark:text-white/55">: <code>{a.from_value}</code> → <code>{a.to_value}</code></span>
                  )}
                  {!a.from_value && a.to_value && (
                    <span className="text-slate-500 dark:text-white/55">: <code>{a.to_value}</code></span>
                  )}
                  <div className="text-slate-400 dark:text-white/30">{new Date(a.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Danger */}
        <div className="px-5 py-4">
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Delete this task? This can\'t be undone from the UI.')) {
                onDelete(task.task_id);
              }
            }}
            data-testid="tasks-drawer-delete"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-2 rounded border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
          >
            <Trash2 size={11} />
            Delete task
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-center gap-4">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 w-20 shrink-0">{label}</div>
      {children}
    </div>
  );
}

// ─────────────────── Page ───────────────────

export default function Tasks() {
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effects */
  const { user } = useAuth();
  const [board, setBoard] = useState({ groups: [], totals: { total: 0, open: 0, done: 0, overdue: 0 } });
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // selected task object
  const [groupBy, setGroupBy] = useState('status');
  const [q, setQ] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      const { data } = await api.get('/tasks/board', { params });
      // Client-side filter pass — keeps the UI snappy and the API simple.
      let groups = data.groups;
      if (q || filterAssignee || filterPriority) {
        const qn = q.toLowerCase();
        groups = groups.map((g) => ({
          ...g,
          tasks: g.tasks.filter((t) => {
            if (qn && !t.title.toLowerCase().includes(qn)) return false;
            if (filterAssignee === 'me' && t.assignee_user_id !== user?.user_id) return false;
            if (filterAssignee === 'unassigned' && t.assignee_user_id) return false;
            if (filterAssignee && filterAssignee !== 'me' && filterAssignee !== 'unassigned' && t.assignee_user_id !== filterAssignee) return false;
            if (filterPriority && t.priority !== filterPriority) return false;
            return true;
          }),
        })).map((g) => ({ ...g, count: g.tasks.length }));
      }
      setBoard({ ...data, groups });
    } catch (err) {
      console.error('Tasks.refresh failed', err);
      toast.error('Could not load tasks');
    } finally {
      setLoading(false);
    }
  }, [groupBy, q, filterAssignee, filterPriority, user?.user_id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    api.get('/tasks/meta/assignees').then(({ data }) => setAssignees(data)).catch(() => {});
  }, []);

  const addTask = async (payload) => {
    try {
      await api.post('/tasks', payload);
      toast.success('Task added.');
      refresh();
    } catch (err) {
      console.error('Tasks.addTask failed', err);
      toast.error(err?.response?.data?.detail || 'Could not add task.');
    }
  };
  const updateTask = async (task_id, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${task_id}`, patch);
      if (open?.task_id === task_id) setOpen(data);
      refresh();
    } catch (err) {
      console.error('Tasks.updateTask failed', err);
      toast.error(err?.response?.data?.detail || 'Update failed.');
    }
  };
  const deleteTask = async (task_id) => {
    try {
      await api.delete(`/tasks/${task_id}`);
      toast.success('Task deleted.');
      setOpen(null);
      refresh();
    } catch (err) {
      console.error('Tasks.deleteTask failed', err);
      toast.error('Could not delete task.');
    }
  };
  const addComment = async (task_id, body) => {
    try {
      await api.post(`/tasks/${task_id}/comments`, { body });
      const { data } = await api.get(`/tasks/${task_id}`);
      setOpen(data);
      refresh();
    } catch (err) {
      console.error('Tasks.addComment failed', err);
      toast.error('Could not post comment.');
    }
  };

  const totals = board.totals;

  return (
    <div data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="ve-eyebrow text-[var(--ve-violet-bright)]">Tasks</div>
          <h1 className="display text-2xl sm:text-3xl md:text-4xl mt-2 font-bold">Plan. Assign. Ship.</h1>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-2 max-w-2xl">
            An embedded task board for the whole tenant — Monday-style. Click any pill to cycle it, drag-drop coming next.
          </p>
        </div>
        <Link
          to="/dashboard/tasks/inbox"
          data-testid="tasks-link-inbox"
          className="self-start inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-200 dark:border-white/10 hover:border-[var(--ve-violet-bright)] text-[10px] uppercase tracking-[0.22em] font-bold text-slate-700 dark:text-white/75 hover:text-[var(--ve-violet-bright)] transition-colors"
        >
          <InboxIcon size={13} />
          My inbox
        </Link>
      </div>

      {/* KPI strip */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Kpi label="Total" value={totals.total} testid="tasks-kpi-total" />
        <Kpi label="Open" value={totals.open} testid="tasks-kpi-open" tone="violet" />
        <Kpi label="Done" value={totals.done} testid="tasks-kpi-done" tone="emerald" />
        <Kpi label="Overdue" value={totals.overdue} testid="tasks-kpi-overdue" tone="rose" />
      </div>

      {/* Filter bar */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search tasks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="tasks-search"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 dark:border-white/10 rounded bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
          />
        </label>
        <select
          value={filterAssignee}
          onChange={(e) => setFilterAssignee(e.target.value)}
          data-testid="tasks-filter-assignee"
          className="text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1.5 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
        >
          <option value="">All assignees</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
          {assignees.map((u) => (
            <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
          ))}
        </select>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          data-testid="tasks-filter-priority"
          className="text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1.5 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
        >
          <option value="">All priorities</option>
          {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
        </select>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          data-testid="tasks-groupby"
          className="text-sm border border-slate-200 dark:border-white/10 rounded px-2 py-1.5 bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-[var(--ve-violet-bright)]"
        >
          <option value="status">Group: Status</option>
          <option value="priority">Group: Priority</option>
          <option value="assignee">Group: Assignee</option>
          <option value="group">Group: Section</option>
        </select>
      </div>

      {/* Board */}
      <div className="mt-6 space-y-3">
        {loading && board.groups.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
        )}
        {!loading && board.groups.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">
            <AlertCircle size={24} className="inline mb-2 opacity-50" />
            <div>No tasks yet. Add your first one.</div>
          </div>
        )}
        {board.groups.map((g) => (
          <Group
            key={g.key}
            group={g}
            onUpdate={updateTask}
            onOpen={setOpen}
            onAddTask={addTask}
          />
        ))}
      </div>

      {open && (
        <TaskDrawer
          task={open}
          assignees={assignees}
          onClose={() => setOpen(null)}
          onUpdate={updateTask}
          onDelete={deleteTask}
          onComment={addComment}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate: 'border-slate-200 dark:border-white/10 text-slate-900 dark:text-white',
    violet: 'border-[var(--ve-violet-bright)] text-[var(--ve-violet-bright)] bg-violet-50/40 dark:bg-violet-500/[0.06]',
    emerald: 'border-emerald-200 text-emerald-700 bg-emerald-50/40 dark:bg-emerald-500/[0.06]',
    rose: 'border-rose-200 text-rose-700 bg-rose-50/40 dark:bg-rose-500/[0.06]',
  };
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 sm:p-4 ${tones[tone]}`}>
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{label}</div>
      <div className="display text-2xl sm:text-3xl mt-2 font-bold">{value}</div>
    </div>
  );
}
